import { Shimmer } from "@/components/ai-elements/shimmer"
import {
  SourceCardRow,
  type SourceItem,
} from "@/components/ai-elements/sources-custom"
import {
  Task,
  TaskContent,
  TaskItem,
  TaskItemFile,
  TaskTrigger,
} from "@/components/ai-elements/task"
import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { ApiCitation } from "@/types/questionnaire/api"
import {
  AlertCircleIcon,
  CheckCircle2Icon,
  ChevronDownIcon,
  Loader2Icon,
} from "lucide-react"
import React, { useState } from "react"
import { ToolStep, TurnBlock } from "../types"
import { presentTool, resourceLabel } from "./toolPresenters"

type ToolsBlock = Extract<TurnBlock, { kind: "tools" }>

/** Turns a step's citations into source pills. Supplied by the message, so a
 * source on a tool row opens exactly what the same source under the answer
 * opens. Without it, a row lists no sources. */
type ToSourceItems = (citations: ApiCitation[]) => SourceItem[]

interface ToolActivityGroupProps {
  block: ToolsBlock
  className?: string
  toSourceItems?: ToSourceItems
}

/** The tool labels are written as progress messages ("Searching your
 * documents …"), which is right while a step runs and reads oddly once it has
 * settled — especially followed by a detail clause ("… … — 6 questionnaires").
 * Display-only, so it lives here rather than in the translation files, which
 * would otherwise need a second phrasing of every label. */
const withoutTrailingEllipsis = (label: string) =>
  label.replace(/[\s.…]+$/u, "")

/**
 * Resolve the detail line: a `detailKey` (the two structurally-derivable
 * shapes — a paged list or a bare count) wins when present and is localized
 * with `resourceLabel`'s own key-with-fallback lookup; otherwise fall back to
 * the prose `detail` string (backend `summary`/`error`, English either way —
 * see `toolPresenters.ts` for why those stay unkeyed).
 */
function resolveDetail(
  step: ToolStep,
  presentation: ReturnType<typeof presentTool>,
  translate: (
    key: TranslationKeys,
    params?: Record<string, string | number>,
  ) => string,
): string | undefined {
  const detailKey = step.detailKey ?? presentation.detailKey
  const params = step.detailParams ?? presentation.detailParams
  if (!detailKey) {
    return step.detail ?? presentation.detail
  }
  return translate(detailKey as TranslationKeys, {
    shown: params?.shown ?? 0,
    total: params?.total ?? 0,
    count: params?.count ?? 0,
    resource: params?.resource ? resourceLabel(params.resource, translate) : "",
  })
}

const useToolLabel = () => {
  const { translate } = useTranslation()
  return (step: ToolStep) => {
    const presentation = presentTool(step.tool, step.input, step.result)
    const label = presentation.labelKey
      ? translate(presentation.labelKey)
      : presentation.labelFallback
    return {
      label: step.status === "running" ? label : withoutTrailingEllipsis(label),
      // A rehydrated step carries these already derived and has no
      // `input`/`result` to derive them from.
      detail: resolveDetail(step, presentation, translate),
      chips: step.chips ?? presentation.chips,
    }
  }
}

const ToolStepRow: React.FC<{
  step: ToolStep
  toSourceItems?: ToSourceItems
}> = ({ step, toSourceItems }) => {
  const toolLabel = useToolLabel()
  const { label, detail, chips } = toolLabel(step)
  const sources =
    toSourceItems && step.citations?.length ? toSourceItems(step.citations) : []

  return (
    <TaskItem className="flex flex-col gap-1">
      <div className="flex items-start gap-2">
        {step.status === "running" && (
          <Loader2Icon className="mt-0.5 size-3.5 shrink-0 animate-spin text-primary" />
        )}
        {step.status === "done" && (
          <CheckCircle2Icon className="mt-0.5 size-3.5 shrink-0 text-success" />
        )}
        {step.status === "error" && (
          <AlertCircleIcon className="mt-0.5 size-3.5 shrink-0 text-destructive" />
        )}
        <div className="min-w-0 flex-1">
          {step.status === "running" ? (
            <Shimmer duration={1}>{label}</Shimmer>
          ) : (
            <span className={step.status === "error" ? "text-destructive" : ""}>
              {label}
            </span>
          )}
          {detail && (
            <span className="text-muted-foreground/80"> — {detail}</span>
          )}
        </div>
      </div>
      {chips.length > 0 && (
        <div className="ml-5 flex flex-wrap gap-1">
          {chips.map((chip, index) => (
            // Index in the key, not just the chip text: two chips can
            // legitimately be identical (load_tools asked for the same name
            // twice, or two different inputs clip to the same 200 chars).
            <TaskItemFile
              key={`${chip}-${index}`}
              className="max-w-[220px]"
              title={chip}
            >
              {/* The ellipsis goes on this span: `text-overflow` does nothing
                  on the chip itself, which is a flex container, so the text
                  was cut hard at its border. */}
              <span className="min-w-0 truncate">{chip}</span>
            </TaskItemFile>
          ))}
        </div>
      )}
      {sources.length > 0 && <SourceCardRow className="ml-5" items={sources} />}
    </TaskItem>
  )
}

/**
 * One run of adjacent tool calls, collapsed to a single row.
 *
 * Grouping is what keeps a turn that ran four tools to a couple of rows rather
 * than four. The model's own narration is NOT in here — it renders as ordinary
 * prose around these groups, because it is worth reading; what made it look
 * bad before was consecutive segments arriving concatenated, not their
 * presence.
 */
export const ToolActivityGroup: React.FC<ToolActivityGroupProps> = ({
  block,
  className,
  toSourceItems,
}) => {
  const { translate } = useTranslation()
  const toolLabel = useToolLabel()
  const presented = block.steps.map(toolLabel)
  // A disclosure that reveals a copy of its own label is a dead control: a
  // lone step with nothing derived from its result (`load_tools`, whose result
  // describes the model's own plumbing, is the common case) had a chevron
  // opening onto the same words. Several steps always earn one, because the
  // trigger only names the last.
  const hasExpandableContent =
    block.steps.length > 1 ||
    presented.some((step) => step.detail || step.chips.length > 0) ||
    (toSourceItems !== undefined &&
      block.steps.some((step) => step.citations?.length))
  // From the steps, not `endedAt`: a group stays open for more tools until
  // narration arrives (see `appendText`), so `endedAt` lags the last step
  // settling and would keep the spinner up after everything had finished.
  const runningStep = block.steps.find((step) => step.status === "running")
  const isRunning = runningStep !== undefined
  // A failure inside a collapsed group would otherwise be invisible: the row
  // reads as a plain "did some things" and the user has no reason to open it.
  const hasError = block.steps.some((step) => step.status === "error")
  // Collapsed from the start and left alone: opening on its own and shutting
  // again a second later moved the page under the reader for something they
  // had not asked to see. The shimmering trigger already says work is
  // happening; the detail is one click away when it is wanted.
  const [isOpen, setIsOpen] = useState(false)

  const seconds =
    block.endedAt !== undefined
      ? Math.max(1, Math.round((block.endedAt - block.startedAt) / 1000))
      : undefined

  // The group names what it DID, the way Claude Code's rows do — a bare step
  // count says nothing about which part of the turn this was. A single tool is
  // its own label; several get the last one (the most recent thing done) plus
  // the count and elapsed time.
  const summary = (() => {
    const lastLabel = presented[presented.length - 1]?.label ?? ""
    if (block.steps.length === 1) {
      return lastLabel
    }
    const steps = translate("AI_CHAT/ACTIVITY/STEPS", {
      count: block.steps.length,
    })
    return seconds !== undefined
      ? `${lastLabel} · ${steps} · ${translate("AI_CHAT/ACTIVITY/WORKED_FOR", { seconds })}`
      : `${lastLabel} · ${steps}`
  })()

  // Only once settled: while a step runs it has no result to derive anything
  // from, and switching from a plain row to a collapsible mid-stream would pop
  // a chevron into existence under the cursor.
  if (!isRunning && !hasExpandableContent) {
    return (
      <div
        data-testid="tool-activity-group"
        className={cn(
          "my-S flex items-center gap-2 text-sm text-muted-foreground",
          className,
        )}
      >
        {/* No success marker, for the same reason the expandable trigger has
            none: these rows are meant to sit quietly beside the prose. */}
        {hasError && (
          <AlertCircleIcon className="size-3.5 shrink-0 text-destructive" />
        )}
        <span data-testid="tool-activity-summary">{summary}</span>
      </div>
    )
  }

  return (
    <Task
      open={isOpen}
      onOpenChange={setIsOpen}
      className={cn("my-S", className)}
      data-testid="tool-activity-group"
    >
      <TaskTrigger title={summary}>
        <button
          type="button"
          data-testid="tool-activity-trigger"
          className="flex w-full cursor-pointer items-center gap-2 text-left text-sm text-muted-foreground transition-colors hover:text-primary"
        >
          {/* No icon on a healthy group: it is a quiet aside, and a decorative
              mark pulled the eye to the one part of the turn that is meant to
              stay out of the way. A failure still gets one, because a
              collapsed row would otherwise hide it completely. */}
          {hasError && (
            <AlertCircleIcon className="size-3.5 shrink-0 text-destructive" />
          )}
          {runningStep ? (
            <Shimmer duration={1}>{toolLabel(runningStep).label}</Shimmer>
          ) : (
            <span data-testid="tool-activity-summary">{summary}</span>
          )}
          <ChevronDownIcon
            className={cn(
              "size-3.5 shrink-0 transition-transform",
              isOpen && "rotate-180",
            )}
          />
        </button>
      </TaskTrigger>
      <TaskContent className="mt-2">
        {block.steps.map((step) => (
          <ToolStepRow
            key={step.toolCallId}
            step={step}
            toSourceItems={toSourceItems}
          />
        ))}
      </TaskContent>
    </Task>
  )
}
